import React, { useEffect, useState } from "react";
import { Form, Input, Upload, message, Select, Button, Card, Typography, Divider, Row, Col } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Space } from "antd";
import axios from "axios";
import SummaryApi from "../../../common";
import "../../../Styles/SellerStylings/UploadProduct.css";

const { Option } = Select;
const { Title } = Typography;

const currencyOptions = [{ label: "₹ INR", value: "INR" }];

function UploadProduct({ token, onSuccess, editMode = false, initialValues = {} }) {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideo, setExistingVideo] = useState(null);
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(SummaryApi.getCategories.url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(res.data.success ? res.data.categories || [] : []);
      } catch (err) {
        console.error("Error fetching categories:", err);
        message.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, [token]);


  // Prefill form for edit mode
  useEffect(() => {
    if (!editMode) {
      form.resetFields();
      setExistingImages([]);
      setExistingVideo(null);
      return;
    }

   if (editMode && initialValues && initialValues.id) {
    console.log("EDIT INITIAL VALUES:", initialValues);

  form.setFieldsValue({
    name: initialValues.name,
    category: initialValues.category_name,   // ✔ FIX
    actual_price: parseInt(initialValues.actual_price),
    actual_currency: "INR",
    selling_price: parseInt(initialValues.selling_price),
    selling_currency: "INR",
    description: initialValues.description,
    youtube: initialValues.youtube || "",
    facebook: initialValues.facebook || "",
    instagram: initialValues.instagram || "",
  stock: initialValues.stock,
    deliveryOption: initialValues.deliveryOption,
  });


      // Prefill images
      let images = [];
      try {
        images = typeof initialValues.images === "string"
          ? JSON.parse(initialValues.images || "[]")
          : initialValues.images || [];
      } catch (err) {
        console.error("Error parsing images:", err);
      }
      setExistingImages(images);

      const imageList = images.map((url, index) => ({
        uid: String(index),
        name: `image-${index}`,
        status: "done",
        url: url.startsWith("http") ? url : `${BASE_URL}${url}`,
        thumbUrl: url.startsWith("http") ? url : `${BASE_URL}${url}`,
      }));
      form.setFieldsValue({ images: imageList });

      // Prefill video
      if (initialValues.video) {
        const videoUrl = initialValues.video.startsWith("http") ? initialValues.video : `${BASE_URL}${initialValues.video}`;
        setExistingVideo(videoUrl);
        form.setFieldsValue({
          video: [{
            uid: "-1",
            name: "existing_video",
            status: "done",
            url: videoUrl,
          }]
        });
      }
    }
  }, [editMode, initialValues, form]);

  // Handle form submit
  const handleFinish = async (values) => {
    const formData = new FormData();

    // Append non-file fields
    Object.keys(values).forEach((key) => {
      if (key !== "images" && key !== "video") {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      }
    });




    // Handle images
    if (values.images && values.images.length > 0) {
      const existingUrls = [];
      values.images.forEach((file) => {
        if (file.originFileObj) {
          formData.append("images", file.originFileObj);
        } else if (file.url && file.status === "done") {
          existingUrls.push(file.url.replace(BASE_URL, ""));
        }
      });
      if (existingUrls.length > 0) {
        formData.append("existingImagesToKeep", JSON.stringify(existingUrls));
      }
    } else if (editMode) {
      existingImages.forEach((img) => {
        formData.append("existingImagesToKeep", img);
      });
    }

    // Handle video
    if (values.video && values.video.length > 0) {
      values.video.forEach((file) => {
        if (file.originFileObj) {
          formData.append("video", file.originFileObj);
        } else if (file.url && file.status === "done") {
          formData.append("existingVideoToKeep", file.url.replace(BASE_URL, ""));
        }
      });
    } else if (editMode && existingVideo) {
      formData.append("existingVideoToKeep", existingVideo.replace(BASE_URL, ""));
    }

    try {
      if (editMode) {
        await axios.put(`${SummaryApi.updateProduct.url}/${initialValues.id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Product updated successfully");
      } else {
        await axios.post(SummaryApi.uploadProduct.url, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Product uploaded successfully");
        form.resetFields();
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Upload/update failed:", err);
      message.error("Failed to upload/update product");
    }
  };

  return (
    <div className="upload-container">
      <Card className="upload-card" bordered={false}>
        <Title level={3}>{editMode ? "Update Product" : "Upload New Product"}</Title>
        <Divider />
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          {/* Product Name */}
          <Form.Item name="name" label="Product Name" rules={[{ required: true, message: "Please enter product name" }]}>
            <Input placeholder="Enter product name" />
          </Form.Item>

          {/* Category */}
          <Form.Item name="category" label="Category" rules={[{ required: true, message: "Please select a category" }]}>
            <Select placeholder="Select a category">
              {categories.map((cat) => <Option key={cat.id} value={cat.name}>{cat.name}</Option>)}
            </Select>
          </Form.Item>

          {/* Prices */}
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item name="actual_currency" label="Currency" rules={[{ required: true }]} initialValue="INR">
                <Select>{currencyOptions.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item name="actual_price" label="Actual Price" rules={[{ required: true, message: "Enter actual price" }]}>
                <Input type="number" placeholder="Enter actual price" onWheel={(e) => e.target.blur()} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={6}>
              <Form.Item name="selling_currency" label="Currency" rules={[{ required: true }]} initialValue="INR">
                <Select>{currencyOptions.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item name="selling_price" label="Selling Price" rules={[{ required: true, message: "Enter selling price" }]}>
                <Input type="number" placeholder="Enter selling price"  onWheel={(e) => e.target.blur()}/>
              </Form.Item>
            </Col>
          </Row>

          {/* Description */}
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Enter product description" />
          </Form.Item>
           {/* Social Media Links */}
{/* Social Media Links */}
<Form.Item name="youtube" label="YouTube Link">
  <Input placeholder="Enter YouTube URL" />
</Form.Item>

<Form.Item name="facebook" label="Facebook Link">
  <Input placeholder="Enter Facebook URL" />
</Form.Item>

<Form.Item name="instagram" label="Instagram Link">
  <Input placeholder="Enter Instagram URL" />
</Form.Item>



          {/* Stock */}
          <Form.Item name="stock" label="Stock" rules={[{ required: true, message: "Enter stock quantity" }]}>
            <Input type="number" placeholder="Enter available stock"  onWheel={(e) => e.target.blur()}/>
          </Form.Item>

          {/* Delivery */}
          <Form.Item name="deliveryOption" label="Delivery Option" rules={[{ required: true, message: "Please select a delivery option" }]}>
            <Select placeholder="Choose delivery type">
              <Option value="store_pickup">Store Pickup</Option>
              <Option value="home_delivery">Home Delivery</Option>
              <Option value="both">Both</Option>
            </Select>
          </Form.Item>

          {/* Image Upload */}
          <Form.Item
            name="images"
            label="Upload Images (Max 4)"
            rules={editMode ? [] : [{ required: true, message: "Please upload at least 1 image" }]}
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          >
            <Upload beforeUpload={() => false} multiple maxCount={4} listType="picture-card">
              <div>
                <UploadOutlined />
                <div style={{ fontSize: 12 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>

          {/* Video Upload */}
          <Form.Item
            name="video"
            label="Upload Video (Max 5MB)"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          >
            <Upload
              beforeUpload={(file) => {
                if (file.size > 5 * 1024 * 1024) {
                  message.error("Video size must be ≤ 5MB");
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              maxCount={1}
              accept="video/*"
              listType="text"
            >
              <Button icon={<UploadOutlined />}>Select Video</Button>
            </Upload>
          </Form.Item>

          {/* Submit */}
          <Form.Item>
            <Button style={{ background: "#1129ce", color: "white" }} htmlType="submit" block>
              {editMode ? "Update Product" : "Upload Product"}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default UploadProduct;
